'use client'

import clsx from 'clsx'
import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useState, Suspense } from 'react'
import { LayoutContext } from './layout-context'
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp'
import './layout.scss'

function LayoutContent({ children }: { children: React.ReactNode }) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const isTelegramWebApp = useTelegramWebApp()

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') {
      return false
    }

    if (window.innerWidth < 1280) {
      return false
    }

    const storedValue = window.localStorage.getItem('sidebarOpen')
    return storedValue ? JSON.parse(storedValue) : false
  })

  const onSidebarOpenChange = useCallback((value: boolean) => {
    setSidebarOpen(value)

    if (window.innerWidth >= 1280) {
      window.localStorage.setItem('sidebarOpen', JSON.stringify(value))
    }
  }, [])

  useEffect(() => {
    const matchMedia = window.matchMedia('(min-width: 1280px)')

    const handleChange = () => {
      if (matchMedia.matches) {
        const storedValue = window.localStorage.getItem('sidebarOpen')
        setSidebarOpen(storedValue ? JSON.parse(storedValue) : false)
      } else {
        setSidebarOpen(false)
      }
    }

    handleChange()

    matchMedia.addEventListener('change', handleChange)

    return () => {
      matchMedia.removeEventListener('change', handleChange)
    }
  }, [])

  useEffect(() => {
    if (window.innerWidth < 1280) {
      setSidebarOpen(false)
    }
  }, [pathname, searchParams])

  const contextValue = useMemo(
    () => ({
      sidebarOpen,
      onSidebarOpenChange,
    }),
    [onSidebarOpenChange, sidebarOpen]
  )

  return (
    <LayoutContext.Provider value={contextValue}>
      <div
        className={clsx(
          'layout',
          sidebarOpen && 'layout--sidebar-open',
          isTelegramWebApp && 'layout--telegram'
        )}
      >
        {isTelegramWebApp && <div className='layout__telegram-spacer' />}
        {children}
      </div>
    </LayoutContext.Provider>
  )
}

export default function Layout({ children }: { children: React.ReactNode }) {
  return (
    <Suspense>
      <LayoutContent>{children}</LayoutContent>
    </Suspense>
  )
}
